// import cl from 'classnames'
import {
  formatPrice, ImageSquare, PaginationButtons, parseStringToNumberAndApplyOnChange, TableBodyContainer,
  TableBodyLine,
  TableBodyLineProps, TableContainer,
  TableHeader,
  TableHeaderProps, Text1
} from '@touche-lts/panel-ui/dist'
import { TableHorizontalScrollControls } from 'components'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'mobx-stores/store-provider'
import { ProductsTableSortProps } from 'mobx-stores/utils'
import { ProductsTableEntityDTO } from 'models'
import moment from 'moment'
import { getMyStoreProductViewPath } from 'routes'
import { useRef, useState } from 'react'
import styles from './products-table.module.scss'
import { ProductsTableProps } from './products-table.types'
import { productsTableWidths } from './products-table.utils'

const ProductsTableComponent = (props: ProductsTableProps) => {
  const {
    containerProps,
    isColumnMarked,
    getSortDirection,
    handleSortDirectionChange,
    searchProps,
    onSearchPropsChange,
    searchResults,
    paginationInfo,
    onPageSelect,
    hideStoreColumn
  } = props

  const tableBodyContainerRef = useRef<HTMLDivElement>(null)
  const [tableHeaderMarginLeft, setTableHeaderMarginLeft] = useState<number>(0)
  const [tableBodyScrollWidth, setTableBodyScrollWidth] = useState<number>(undefined)

  const {
    generalStore: { showError },
    routingStore: { push },
    userStore: { managementStoreId }
  } = useRootStore()

  const tableColumns: TableHeaderProps['tableColumns'] = [
    {
      label: 'id',
      widthPx: productsTableWidths.id,
      isColumnMarked: isColumnMarked(ProductsTableSortProps.ID),
      sortDirection: getSortDirection(ProductsTableSortProps.ID),
      setSortDirection: handleSortDirectionChange(ProductsTableSortProps.ID),
      inputProps: {
        placeholder: 'Search...',
        value: searchProps.id,
        onChange: parseStringToNumberAndApplyOnChange({
          showError,
          onChange: id => onSearchPropsChange({ id })
        })
      }
    },
    {
      label: 'product',
      widthPx:productsTableWidths.product,
      isColumnMarked: isColumnMarked(ProductsTableSortProps.PRODUCT_NAME),
      sortDirection: getSortDirection(ProductsTableSortProps.PRODUCT_NAME),
      setSortDirection: handleSortDirectionChange(
        ProductsTableSortProps.PRODUCT_NAME
      ),
      inputProps: {
        placeholder: 'Search...',
        value: searchProps.productName,
        onChange: productName => onSearchPropsChange({ productName })
      }
    },
    {
      label: 'store',
      widthPx: 180,
      isColumnMarked: isColumnMarked(ProductsTableSortProps.STORE_NAME),
      sortDirection: getSortDirection(ProductsTableSortProps.STORE_NAME),
      setSortDirection: handleSortDirectionChange(
        ProductsTableSortProps.STORE_NAME
      ),
      inputProps: {
        placeholder: 'Search...',
        value: searchProps.storeName,
        onChange: storeName => onSearchPropsChange({ storeName })
      }
    },
    {
      label: 'active skus',
      widthPx: productsTableWidths.activeSkus,
      isColumnMarked: isColumnMarked(ProductsTableSortProps.ACTIVE_SKUS),
      sortDirection: getSortDirection(ProductsTableSortProps.ACTIVE_SKUS),
      setSortDirection: handleSortDirectionChange(
        ProductsTableSortProps.ACTIVE_SKUS
      ),
      sliderFilterProps: {
        label: 'active skus',
        min: 0,
        max: 100,
        value: [searchProps.minSKUs, searchProps.maxSKUs],
        onChange: ([minSKUs, maxSKUs]) =>
          onSearchPropsChange({ minSKUs, maxSKUs })
      }
    },
    {
      label: 'inventory',
      widthPx: 180,
      isColumnMarked: isColumnMarked(ProductsTableSortProps.INVENTORY),
      sortDirection: getSortDirection(ProductsTableSortProps.INVENTORY),
      setSortDirection: handleSortDirectionChange(
        ProductsTableSortProps.INVENTORY
      ),
      sliderFilterProps: {
        label: 'items in stock',
        min: 0,
        max: 100,
        value: [searchProps.minStock, searchProps.maxStock],
        onChange: ([minStock, maxStock]) =>
          onSearchPropsChange({ minStock, maxStock })
      }
    },
    {
      label: 'sold',
      widthPx: 180,
      isColumnMarked: isColumnMarked(ProductsTableSortProps.SOLD),
      sortDirection: getSortDirection(ProductsTableSortProps.SOLD),
      setSortDirection: handleSortDirectionChange(ProductsTableSortProps.SOLD),
      sliderFilterProps: {
        label: 'items sold',
        min: 0,
        max: 100,
        value: [searchProps.minSold, searchProps.maxStock],
        onChange: ([minSold, maxStock]) =>
          onSearchPropsChange({ minSold, maxStock })
      }
    },
    {
      label: 'revenue',
      widthPx: 180,
      isColumnMarked: isColumnMarked(ProductsTableSortProps.TOTAL_REVENUE),
      sortDirection: getSortDirection(ProductsTableSortProps.TOTAL_REVENUE),
      setSortDirection: handleSortDirectionChange(
        ProductsTableSortProps.TOTAL_REVENUE
      ),
      sliderFilterProps: {
        label: 'usd',
        min: 0,
        max: 100,
        value: [searchProps.minRevenue, searchProps.maxRevenue],
        onChange: ([minRevenue, maxRevenue]) =>
          onSearchPropsChange({ minRevenue, maxRevenue })
      }
    },
    {
      label: 'department',
      widthPx: 180,
      isColumnMarked: isColumnMarked(ProductsTableSortProps.DEPARTMENT),
      sortDirection: getSortDirection(ProductsTableSortProps.DEPARTMENT),
      setSortDirection: handleSortDirectionChange(
        ProductsTableSortProps.DEPARTMENT
      ),
      inputProps: {
        placeholder: 'Search...',
        value: searchProps.departmentName,
        onChange: departmentName => onSearchPropsChange({ departmentName })
      }
    },
    {
      label: 'category',
      widthPx: 180,
      isColumnMarked: isColumnMarked(ProductsTableSortProps.CATEGORY),
      sortDirection: getSortDirection(ProductsTableSortProps.CATEGORY),
      setSortDirection: handleSortDirectionChange(
        ProductsTableSortProps.CATEGORY
      ),
      inputProps: {
        placeholder: 'Search...',
        value: searchProps.categoryName,
        onChange: categoryName => onSearchPropsChange({ categoryName })
      }
    },
    {
      label: 'subcategory',
      widthPx: 180,
      isColumnMarked: isColumnMarked(ProductsTableSortProps.SUBCATEGORY),
      sortDirection: getSortDirection(ProductsTableSortProps.SUBCATEGORY),
      setSortDirection: handleSortDirectionChange(
        ProductsTableSortProps.SUBCATEGORY
      ),
      inputProps: {
        placeholder: 'Search...',
        value: searchProps.subcategoryName,
        onChange: subcategoryName => onSearchPropsChange({ subcategoryName })
      }
    },
    {
      label: 'type',
      widthPx: 180,
      isColumnMarked: isColumnMarked(ProductsTableSortProps.TYPE),
      sortDirection: getSortDirection(ProductsTableSortProps.TYPE),
      setSortDirection: handleSortDirectionChange(
        ProductsTableSortProps.TYPE
      ),
      inputProps: {
        placeholder: 'Search...',
        value: searchProps.type,
        onChange: type => onSearchPropsChange({ type })
      }
    },
    {
      label: 'added to marketplace',
      widthPx: 180,
      isColumnMarked: isColumnMarked(ProductsTableSortProps.ADDED_DATE),
      sortDirection: getSortDirection(ProductsTableSortProps.ADDED_DATE),
      setSortDirection: handleSortDirectionChange(
        ProductsTableSortProps.ADDED_DATE
      ),
      periodFilterProps: {
        period: searchProps.addedOnPeriod,
        setPeriod: addedOnPeriod => onSearchPropsChange({ addedOnPeriod }),
        className: styles.periodFilter
      }
    }
  ]
  if (hideStoreColumn) {
    tableColumns.splice(2, 1)
  }

  const getBodyLineCells = (
    product: ProductsTableEntityDTO
  ): TableBodyLineProps['cells'] => {
    const cells: TableBodyLineProps['cells'] = [
      {
        children: product.id,
        widthPx: productsTableWidths.id,
        isColumnMarked: isColumnMarked(ProductsTableSortProps.ID),
        onClick: () => push(getMyStoreProductViewPath(product.id, managementStoreId))
      },
      {
        children: (
          <>
            <ImageSquare src={product.image?.url} width={30} />
            <Text1
              colorPreset='dark'
              bold
              overflowEllipsis
              style={{ marginLeft: 8 }}
            >
              {product.name}
            </Text1>
          </>
        ),
        widthPx:productsTableWidths.product,
        isColumnMarked: isColumnMarked(ProductsTableSortProps.PRODUCT_NAME),
        onClick: () => push(getMyStoreProductViewPath(product.id, managementStoreId))
      },
      {
        children: (
          <Text1 colorPreset='dark' overflowEllipsis>
            {product.storeName}
          </Text1>
        ),
        widthPx: 180,
        isColumnMarked: isColumnMarked(ProductsTableSortProps.STORE_NAME)
      },
      {
        children: product.activeSkuCount,
        widthPx: productsTableWidths.activeSkus,
        isColumnMarked: isColumnMarked(ProductsTableSortProps.ACTIVE_SKUS)
      },
      {
        children: (
          <div>
            <Text1 colorPreset='dark' overflowEllipsis>
              {product.stock || 0} in stock
            </Text1>
            <Text1 colorPreset='dark' overflowEllipsis>
              ({product.variants || 0} variant
              {product.variants === 1 ? '' : 's'})
            </Text1>
          </div>
        ),
        widthPx: 180,
        isColumnMarked: isColumnMarked(ProductsTableSortProps.INVENTORY)
      },
      {
        children: product.sold || 0,
        widthPx: 180,
        isColumnMarked: isColumnMarked(ProductsTableSortProps.SOLD)
      },
      {
        children: formatPrice(product.revenue),
        widthPx: 180,
        isColumnMarked: isColumnMarked(ProductsTableSortProps.TOTAL_REVENUE)
      },
      {
        children: product.department,
        widthPx: 180,
        isColumnMarked: isColumnMarked(ProductsTableSortProps.DEPARTMENT)
      },
      {
        children: product.category,
        widthPx: 180,
        isColumnMarked: isColumnMarked(ProductsTableSortProps.CATEGORY)
      },
      {
        children: product.subcategory,
        widthPx: 180,
        isColumnMarked: isColumnMarked(ProductsTableSortProps.SUBCATEGORY)
      },
      {
        children: product.type,
        widthPx: 180,
        isColumnMarked: isColumnMarked(ProductsTableSortProps.TYPE)
      },
      {
        children: moment(product.addedToStore).format('DD.MM.YY'),
        widthPx: 180,
        isColumnMarked: isColumnMarked(ProductsTableSortProps.ADDED_DATE)
      }
    ]
    if (hideStoreColumn) {
      cells.splice(2, 1)
    }

    return cells
  }

  return (
    <TableContainer className={styles.tableContainer} colorPreset='dark' {...containerProps}>
      <TableHeader 
        colorPreset='dark' 
        tableColumns={tableColumns} 
        style={{
          marginLeft: tableHeaderMarginLeft,
          width: tableBodyScrollWidth,
          zIndex: 1
        }}
      />

      <TableHorizontalScrollControls
        containerRef={tableBodyContainerRef}
        onScrollLeft={(pixels) => setTableHeaderMarginLeft(-pixels)}
        onScrollWidthChange={(scrollWidth) => setTableBodyScrollWidth(scrollWidth)}
      />

      <TableBodyContainer 
        className={styles.tableBodyContainer}
        ref={tableBodyContainerRef}
      >
        {!!searchResults.length && searchResults.map((product, idx) => {
          return (
            <TableBodyLine
              id={idx === 0 ? "#top-row" : ""}
              colorPreset='dark'
              key={product.id}
              style={{
                width: 1890, // Fifxed for now, should be tableBodyScrollWidth
                borderTop: '1px solid #59595980'
              }}
              cells={getBodyLineCells(product)}
            />
          )
        })}
      </TableBodyContainer>
      <PaginationButtons
        colorPreset='dark'
        alwaysShow
        {...paginationInfo}
        onPageSelect={(pageNumber) => {
          const e = document.getElementById("#top-row");
          e.scrollIntoView();
          onPageSelect(pageNumber)
        }}
      />
    </TableContainer>
  )
}

const ProductsTable = observer(ProductsTableComponent)
export { ProductsTable }
